/**
 * ChangePhoneSection Component
 * Change phone number with OTP verification
 */

import { useEffect, useRef, useState } from "react"
import { toast } from "sonner"
import { PhoneNumberInput } from "@/components/forms/phoneInput"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { useAuthStore } from "@/features/auth/store/authStore"
import { useRefreshUserProfile } from "@/features/profile/hooks/useRefreshUserProfile"
import { useTranslation } from "@/language"
import { cn } from "@/lib/utils"
import * as settingsService from "../services/settings.service"

export function ChangePhoneSection() {
  const { t } = useTranslation("settings")
  const user = useAuthStore(state => state.user)
  const { refreshUserProfile } = useRefreshUserProfile()
  const [newPhoneNumber, setNewPhoneNumber] = useState("")
  const [countryCode, setCountryCode] = useState(user?.countryCode || "+1")

  // Verification state
  const [isVerifying, setIsVerifying] = useState(false)
  const [isSendingOtp, setIsSendingOtp] = useState(false)
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false)
  const [resendCountdown, setResendCountdown] = useState(0)
  const [otpError, setOtpError] = useState("")
  const [otp, setOtp] = useState("")

  // OTP refs for focus management
  const otpInputRefs = useRef<(HTMLInputElement | null)[]>([])

  const currentPhone = user?.phoneNumber
    ? `${user.countryCode || ""} ${user.phoneNumber}`
    : t("changePhone.currentPhoneNotSet")

  // Get full phone number for display
  const getFullPhoneNumber = () => {
    return `${countryCode} ${newPhoneNumber}`
  }

  // Countdown timer for resend
  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => setResendCountdown(resendCountdown - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [resendCountdown])

  // Auto-focus first OTP input when verification screen appears
  useEffect(() => {
    if (isVerifying && otpInputRefs.current[0]) {
      otpInputRefs.current[0]?.focus()
    }
  }, [isVerifying])

  // Get phone number without country code prefix
  const getPhoneWithoutCountryCode = (): string => {
    const countryDigits = countryCode.replace("+", "") // "91"
    // Handle case where phone number includes + prefix (e.g., "+917986308256")
    if (newPhoneNumber.startsWith("+")) {
      // Remove the + and check if it starts with country digits
      const phoneWithoutPlus = newPhoneNumber.slice(1)
      if (phoneWithoutPlus.startsWith(countryDigits)) {
        return phoneWithoutPlus.slice(countryDigits.length)
      }
    } else if (newPhoneNumber.startsWith(countryDigits)) {
      return newPhoneNumber.slice(countryDigits.length)
    }
    return newPhoneNumber
  }

  // Check if phone number has actual digits beyond country code
  const hasValidPhoneNumber = (): boolean => {
    if (!newPhoneNumber || !countryCode) return false
    const countryDigits = countryCode.replace("+", "")
    const phoneWithoutCountry = newPhoneNumber.replace(countryDigits, "")
    return phoneWithoutCountry.replace(/\D/g, "").length >= 3
  }

  // Handle sending OTP
  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newPhoneNumber.trim()) {
      toast.error(t("changePhone.validationEnterPhone"))
      return
    }

    setIsSendingOtp(true)
    setOtpError("")

    try {
      await settingsService.sendPhoneVerificationOtp(getPhoneWithoutCountryCode(), countryCode)
      setIsVerifying(true)
      setResendCountdown(60)
      toast.success(t("changePhone.successCodeSent"))
    } catch (error) {
      const err = error as { response?: { data?: { message?: string } } }
      const message = err.response?.data?.message || t("changePhone.errorFailedToSend")
      toast.error(message)
    } finally {
      setIsSendingOtp(false)
    }
  }

  // Handle OTP verification
  const handleVerifyOtp = async (otp: string) => {
    setIsVerifyingOtp(true)
    setOtpError("")

    try {
      await settingsService.verifyPhoneOtp(otp, getPhoneWithoutCountryCode(), countryCode)
      toast.success(t("changePhone.successPhoneChanged"))

      // Refresh user profile to get updated phone number
      await refreshUserProfile()

      // Reset state
      setIsVerifying(false)
      setNewPhoneNumber("")
    } catch (error) {
      const err = error as { response?: { data?: { message?: string } } }
      const message = err.response?.data?.message || t("changePhone.errorInvalidCode")
      setOtpError(message)
      throw new Error(message)
    } finally {
      setIsVerifyingOtp(false)
    }
  }

  // Handle resend OTP
  const handleResendOtp = async () => {
    if (resendCountdown > 0) return

    setIsSendingOtp(true)
    setOtpError("")

    try {
      await settingsService.sendPhoneVerificationOtp(getPhoneWithoutCountryCode(), countryCode)
      setResendCountdown(60)
      toast.success(t("changePhone.successCodeSent"))
    } catch (error) {
      const err = error as { response?: { data?: { message?: string } } }
      const message = err.response?.data?.message || t("changePhone.errorFailedToSend")
      toast.error(message)
    } finally {
      setIsSendingOtp(false)
    }
  }

  // OTP handlers
  const isOtpComplete = otp.length === 6

  const handleOtpChange = (index: number, value: string) => {
    // Only allow numbers
    if (!/^\d*$/.test(value)) return

    // Take only the last character
    const digit = value.slice(-1)

    // Update the value
    const newValue = otp.split("")
    newValue[index] = digit
    const result = newValue.join("").slice(0, 6)
    setOtp(result)

    // Auto-focus next input
    if (digit && index < 5) {
      otpInputRefs.current[index + 1]?.focus()
    }

    // Auto-submit when OTP is complete
    if (result.length === 6) {
      handleVerifyOtp(result).catch(() => {
        // Error is already handled in handleVerifyOtp
      })
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      // Focus previous input
      otpInputRefs.current[index - 1]?.focus()
    }
  }

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData("text/plain")

    // Extract only digits
    const digits = pastedData.replace(/\D/g, "").slice(0, 6)
    setOtp(digits)

    if (digits.length === 6) {
      handleVerifyOtp(digits).catch(() => {
        // Error is already handled in handleVerifyOtp
      })
    }
  }

  const handleResendCode = () => {
    if (resendCountdown > 0) return
    setOtp("")
    handleResendOtp()
  }

  // If in verification mode, show verification screen inline
  if (isVerifying) {
    return (
      <div className="flex flex-col px-4 py-6">
        {/* Main Content */}
        <div className="flex-1 flex items-center justify-center">
          <div className="w-full max-w-md">
            {/* Title */}
            <div className="text-center mb-3">
              <h2 className="text-lg md:text-xl font-semibold leading-5 text-primary">
                {t("changePhone.verifyTitle")}
              </h2>
            </div>

            {/* Subtitle */}
            <div className="text-center mb-8 space-y-1">
              <p className="text-sm text-body">
                {t("changePhone.phoneSent", { phone: getFullPhoneNumber() })}
              </p>
              <p className="text-sm text-body">{t("changePhone.enterCode")}</p>
            </div>

            {/* OTP Input */}
            <div className="flex justify-center gap-1 mb-8">
              {[0, 1, 2, 3, 4, 5].map(index => (
                <input
                  key={index}
                  ref={el => {
                    otpInputRefs.current[index] = el
                  }}
                  id={`otp-${index}`}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={otp[index] || ""}
                  onChange={e => handleOtpChange(index, e.target.value)}
                  onKeyDown={e => handleKeyDown(index, e)}
                  onPaste={handlePaste}
                  disabled={isVerifyingOtp}
                  className={cn(
                    "w-12 h-12 text-center text-xl font-semibold",
                    "border-2 rounded-lg",
                    "focus:outline-none",
                    "disabled:cursor-not-allowed disabled:opacity-50",
                    "transition-all duration-200",
                    otpError
                      ? "border-brand-terracotta focus:border-brand-terracotta focus:ring-brand-terracotta"
                      : "border-1 border-input focus:border-input focus:ring-2 focus:ring-charcoal",
                  )}
                  style={{
                    fontSize: "18px",
                    fontFamily: "inherit",
                  }}
                />
              ))}
            </div>

            {/* Error Message */}
            {otpError && (
              <div className="text-center mb-6">
                <p className="text-sm text-brand-terracotta font-medium">{otpError}</p>
              </div>
            )}

            {/* Resend Link */}
            <div className="text-center mb-8">
              <p className="text-sm text-body">
                {t("changePhone.didntGetCode")}{" "}
                {resendCountdown > 0 ? (
                  <span className="text-primary">
                    {t("changePhone.resendCountdown", { resendCountdown })}
                  </span>
                ) : (
                  <Button
                    type="button"
                    variant="link"
                    onClick={handleResendCode}
                    className="font-semibold underline hover:no-underline p-0 h-auto text-charcoal"
                    disabled={isSendingOtp}
                  >
                    {t("changePhone.resend")}
                  </Button>
                )}
              </p>
            </div>

            {/* Continue Button */}
            <div className="flex justify-center">
              <Button
                type="button"
                variant="primary"
                className="min-w-[200px] h-12 px-8 text-base font-semibold rounded-lg"
                disabled={!isOtpComplete || isVerifyingOtp}
                isLoading={isVerifyingOtp}
                onClick={() => handleVerifyOtp(otp)}
              >
                {t("changePhone.continue")}
              </Button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-7.5">
      {/* Section Header */}
      <div>
        <h1 className="text-lg md:text-xl leading-5 font-semibold text-primary uppercase section-heading">
          {t("changePhone.title")}
        </h1>
        <p className="text-sm leading-6.25 text-primary mt-2.5 md:mt-1.25">
          {t("changePhone.description")}
        </p>
      </div>
      <form onSubmit={handleSendOtp} className="flex flex-col gap-7.5 max-w-md">
        {/* Current Phone Number */}
        <div className="flex flex-col gap-1.25 md:flex-row md:items-center md:gap-2.5 text-sm text-primary">
          <Label className="leading-6.25">{t("changePhone.currentPhone")}</Label>
          <p className="font-semibold leading-6.25">{currentPhone}</p>
        </div>

        {/* New Phone Number */}
        <div className="flex flex-col xl:flex-row xl:items-end gap-5">
          <div className="space-y-2">
            <PhoneNumberInput
              label={t("changePhone.newPhoneLabel")}
              required={true}
              countryCode={countryCode}
              onCountryCodeChange={setCountryCode}
              onPhoneNumberChange={setNewPhoneNumber}
              placeholder={t("changePhone.newPhonePlaceholder")}
            />
          </div>

          <div className="">
            <Button
              type="submit"
              variant="primary"
              size="md"
              className="py-3.75 text-sm font-semibold uppercase flex-1 w-full md:w-[362px] lg:w-[220px]"
              isLoading={isSendingOtp}
              disabled={!hasValidPhoneNumber()}
            >
              {t("changePhone.change")}
            </Button>
          </div>
        </div>
      </form>
    </div>
  )
}
